import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, notification } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { sumPurchaseProdutcs, formatCurrency } from "../../../utils/helper";

/****************************************************
 * ##: Quantity Component
 * @param {Object} data - The data object containing event details.
 * @param {Function} actionHandler - Function to handle actions like updating quantity.
 * @param {boolean} isMobile - Flag to indicate if the view is on a mobile device.
 * History:
 * ??-??-????: Created
 * 27-05-2025: Update v2 (using useMemo and useCallback for performance optimization)
 * 06-06-2026: Fixed unit label not updating on language switch
 ****************************************************/
const Quantity = ({ data, actionHandler, isMobile }) => {
  const { t, i18n } = useTranslation();
  const isValidFormat = data?.format === "online-event" || data?.format === "physical-event" || data?.format === "digital-quantity";

  const qty = data.qty || 1;
  const minQty = 1;

  const statusAction = data?.status?.action;
  const qtyLocked = data?.qty_locked;

  const isDisabled = useMemo(() => {
    return statusAction === "paid" || statusAction === "wait-for-payment" || qtyLocked === true;
  }, [statusAction, qtyLocked]);

  const grandSum = useMemo(() => sumPurchaseProdutcs(data?.purchase_products), [data?.purchase_products]);
  const grandTotal = useMemo(() => grandSum?.price || 0, [grandSum]);

  const maxQtyPerUser = data?.max_qty_per_purchase;
  const maxPurchaseAmount = data?.max_purchase_amount;

  const maxQtyAllowed = useMemo(() => {
    const available = data?.max_qty_available;
    return available > maxQtyPerUser ? maxQtyPerUser : available;
  }, [data?.max_qty_available, maxQtyPerUser]);

  const qtyLabel = useMemo(() => {
    return isDisabled ? (qty === 1 ? t("quantity.unitSingular") : t("quantity.unitPlural")) : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty, isDisabled, i18n.language]);

  const updateQty = useCallback(
    (action) => {
      const singlePrice = grandSum?.single_price || 0;
      const isMaxThresholdReached = grandTotal + singlePrice >= maxPurchaseAmount && maxPurchaseAmount > 0;

      if (isMaxThresholdReached && action === "add") {
        notification.error({
          message: t("quantity.limitReachedTitle"),
          description: t("quantity.limitReachedDescription", { max: formatCurrency(maxPurchaseAmount) }),
        });
        return;
      }

      let newQty = qty;
      if (action === "add") newQty++;
      if (action === "subtract") newQty--;
      if (newQty < 1) newQty = 1;

      actionHandler("qty", newQty);
    },
    [grandSum, grandTotal, maxPurchaseAmount, qty, actionHandler],
  );

  if (!isValidFormat) return <></>;

  return (
    <Card size="small" style={{ marginTop: 14 }} title={<div style={{ textAlign: "center" }}>{t("quantity.title")}</div>} type="inner">
      <div className="qty-container">
        {!isDisabled && (
          <Button type="default" shape="circle" onClick={() => updateQty("subtract")} disabled={qty <= minQty}>
            <MinusOutlined />
          </Button>
        )}
        <div className="qty-indicator">
          {qty} {qtyLabel && <span>{qtyLabel}</span>}
        </div>
        {!isDisabled && (
          <Button type="default" shape="circle" onClick={() => updateQty("add")} disabled={qty >= maxQtyAllowed}>
            <PlusOutlined />
          </Button>
        )}
      </div>
    </Card>
  );
};

export default React.memo(Quantity);
